import React, { useState, useEffect, useMemo } from 'react';
import { Edit, Lock, Trophy, ChevronRight, BarChart3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatTimeRemaining } from '@/lib/dateFormat';
import { CountdownIcon } from '@/components/icons/CountdownIcon';
import { LiveIcon } from '@/components/icons/LiveIcon';
import { CompleteIcon } from '@/components/icons/CompleteIcon';

type GameState = 'upcoming' | 'countdown' | 'live' | 'final';

interface GameCardProps {
  id: string;
  title: string;
  prize: string;
  state: GameState;
  gameDate: Date;
  finalScore?: { rank: number; points: number };
  hasSelections?: boolean;
  isLocked?: boolean;
  totalPicks?: number;
  correctPicks?: number;
  currentPoints?: number;
  onEdit?: () => void;
  onPlay?: () => void;
  onView?: () => void;
  onViewLeaderboard?: () => void;
  className?: string;

  /**
   * Optional override for where the card should navigate when it's clickable.
   * Defaults to `/pickems/${id}/live`.
   */
  liveRoute?: string;
}

const GameCard: React.FC<GameCardProps> = ({
  id,
  title,
  prize,
  state,
  gameDate,
  finalScore,
  hasSelections = false,
  isLocked = false,
  totalPicks = 6,
  correctPicks,
  currentPoints,
  onEdit,
  onPlay,
  onView,
  onViewLeaderboard,
  className = '',
  liveRoute,
}) => {
  console.log(
    'title:',
    title,
    'has selections:',
    hasSelections,
    'total picks:',
    totalPicks,
    'correct picks:',
    correctPicks,
    'current points:',
    currentPoints,
    'state:',
    state
  );

  const { t } = useTranslation();
  const navigate = useNavigate();

  const [timeRemaining, setTimeRemaining] = useState<string>('');

  const canNavigate = useMemo(() => state === 'live' && hasSelections, [state, hasSelections]);

  const targetLiveRoute = liveRoute ?? `/pickems/${id}/live`;

  const handleCardClick = () => {
    // Deprecated: Use a button instead.
    //if (!canNavigate) return;
    //onView();
  };

  const handleCardKeyDown: React.KeyboardEventHandler<HTMLDivElement> = e => {
    if (!canNavigate) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigate(targetLiveRoute);
    }
  };

  const stopCardClick: React.MouseEventHandler<HTMLElement> = e => {
    e.stopPropagation();
  };

  useEffect(() => {
    if (state !== 'countdown') return;

    setTimeRemaining(formatTimeRemaining(gameDate));

    const interval = setInterval(() => {
      const now = new Date();
      const targetDate = typeof gameDate === 'string' ? new Date(gameDate) : gameDate;

      if (targetDate.getTime() <= now.getTime()) {
        clearInterval(interval);
        setTimeRemaining(t('game.time.startingSoon'));
      } else {
        setTimeRemaining(formatTimeRemaining(targetDate));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [gameDate, state, t]);

  const getStartDisplay = () => {
    switch (state) {
      case 'countdown':
        return timeRemaining || formatTimeRemaining(gameDate);
      case 'live':
        return t('game.status.live');
      case 'final':
        return t('game.status.complete');
      default: {
        const now = new Date();
        const targetDate = typeof gameDate === 'string' ? new Date(gameDate) : gameDate;
        const diff = targetDate.getTime() - now.getTime();
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        if (days > 0) return `${days} ${days > 1 ? t('game.time.days') : t('game.time.day')}`;
        const hours = Math.floor(diff / (1000 * 60 * 60));
        if (hours > 0) return `${hours} ${hours > 1 ? t('game.time.hours') : t('game.time.hour')}`;
        return t('game.time.soon');
      }
    }
  };

  const getStartIcon = () => {
    switch (state) {
      case 'countdown':
      case 'upcoming':
        return <CountdownIcon className="w-4 h-4 flex-shrink-0" />;
      case 'live':
        return <LiveIcon className="w-4 h-4 flex-shrink-0" />;
      case 'final':
        return <CompleteIcon className="w-4 h-4 flex-shrink-0" />;
      default:
        return <CountdownIcon className="w-4 h-4 flex-shrink-0" />;
    }
  };

  const getPicksDisplay = () => {
    if (state === 'final' && correctPicks !== undefined) return `${correctPicks}/${totalPicks}`;
    if (state === 'final' && !hasSelections) return `-/${totalPicks}`;
    if (state === 'live' && hasSelections && correctPicks !== undefined) {
      return `${correctPicks}/${totalPicks} ${t('game.status.correct')}`;
    }

    return totalPicks.toString();
  };

  const getOrdinalSuffix = (rank: number): string => {
    const j = rank % 10;
    const k = rank % 100;
    if (j === 1 && k !== 11) return t('game.ordinals.st');
    if (j === 2 && k !== 12) return t('game.ordinals.nd');
    if (j === 3 && k !== 13) return t('game.ordinals.rd');
    return t('game.ordinals.th');
  };

  const getRightContent = () => {
    if (state === 'live') {
      if (hasSelections) {
        return (
          <button
            data-test-id={`edit-picks-btn-${id}`}
            onClick={e => {
              stopCardClick(e);
              onEdit?.();
            }}
            className="flex w-[108px] justify-center items-center gap-1 py-2 px-4 rounded-md border-2 border-[#01A925] bg-transparent text-white font-gilroy font-bold text-sm hover:bg-[#01A925]/10 transition-colors"
          >
            {t('game.actions.view')}
            <Edit className="w-3.5 h-3.5" />
          </button>
        );
      }

      return (
        <div className="flex items-center gap-2">
          <button
            data-test-id={`edit-picks-btn-${id}`}
            onClick={e => {
              stopCardClick(e);
              onEdit?.();
            }}
            className="flex w-[108px] justify-center items-center gap-1 py-2 px-4 rounded-md border-2 border-[#01A925] bg-transparent text-white font-gilroy font-bold text-sm hover:bg-[#01A925]/10 transition-colors"
          >
            {t('game.actions.view')}
          </button>
          <div className="flex w-[42px] h-[42px] p-3 justify-center items-center gap-2 rounded-md bg-[#333]">
            <Lock className="w-[18px] h-[18px] text-text-secondary" />
          </div>
        </div>
      );
    }

    if (state === 'final' && finalScore) {
      return (
        <div className="flex items-center gap-2">
          <span
            data-test-id={`final-points-${id}`}
            className="font-gilroy font-bold text-base text-text-primary"
          >
            {finalScore.points}pts
          </span>
          <div className="h-5 w-px bg-border/20"></div>
          <span
            data-test-id={`user-rank-${id}`}
            className="font-gilroy font-bold text-base text-text-primary flex items-center gap-1"
          >
            {finalScore.rank}
            {getOrdinalSuffix(finalScore.rank)}
            {finalScore.rank === 1 && <Trophy className="w-4 h-4 text-[var(--winner-gold)]" />}
          </span>
        </div>
      );
    }

    if (state === 'countdown') {
      if (hasSelections) {
        return (
          <button
            data-test-id={`edit-picks-btn-${id}`}
            onClick={e => {
              stopCardClick(e);
              onEdit?.();
            }}
            className="flex w-[108px] justify-center items-center gap-1 py-2 px-4 rounded-md border-2 border-[#01A925] bg-transparent text-white font-gilroy font-bold text-sm hover:bg-[#01A925]/10 transition-colors"
          >
            {t('game.actions.edit')}
            <Edit className="w-3.5 h-3.5" />
          </button>
        );
      }

      return (
        <button
          data-test-id={`play-now-btn-${id}`}
          onClick={e => {
            stopCardClick(e);
            onPlay?.();
          }}
          className="flex items-center gap-1 py-3 pl-4 pr-3 rounded-md bg-[#01A925] text-white font-gilroy font-bold text-sm hover:bg-[#01A925]/90 transition-colors"
        >
          {t('game.actions.playNow')}
          <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
        </button>
      );
    }

    if (state === 'upcoming') {
      return (
        <button
          data-test-id={`play-now-btn-${id}`}
          onClick={e => {
            stopCardClick(e);
            onPlay?.();
          }}
          className="flex items-center gap-1 py-3 pl-4 pr-3 rounded-md bg-[#01A925] text-white font-gilroy font-bold text-sm hover:bg-[#01A925]/90 transition-colors"
        >
          {t('game.actions.playNow')}
          <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
        </button>
      );
    }

    return null;
  };

  const getBottomButton = () => {
    if (state === 'final' && hasSelections) {
      return (
        <button
          data-test-id={`leaderboard-btn-${id}`}
          onClick={e => {
            stopCardClick(e);
            (onViewLeaderboard || onView)?.();
          }}
          className="flex items-center gap-1 py-3 pl-4 pr-3 rounded-md bg-[#01A925] text-white font-gilroy font-semibold text-sm hover:bg-[#01A925]/90 transition-colors"
        >
          {t('game.actions.leaderboard')}
          <BarChart3 className="w-4 h-4" />
        </button>
      );
    }

    return null;
  };

  const isWinner = state === 'final' && finalScore?.rank === 1;

  const getCardBackground = () => {
    if (state === 'live' || state === 'final') return 'bg-[rgba(0,0,0,0.3)]';
    return 'bg-[#333333]';
  };

  return (
    <div
      role={canNavigate ? 'button' : undefined}
      tabIndex={canNavigate ? 0 : -1}
      aria-label={canNavigate ? t('game.actions.view') : undefined}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      data-test-id={`game-card-${id}`}
      data-test-state={state}
      data-test-locked={isLocked}
      className={`
        ${getCardBackground()} rounded-xl p-4
        ${state === 'live' ? 'border-2 border-[var(--game-card-v2-live-border)]' : 'border border-border/10'}
        ${isWinner ? 'winner-card-glow' : ''}
        ${isLocked ? 'opacity-60' : ''}
        transition-smooth
        ${canNavigate ? 'cursor-pointer hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-[#01A925]/40' : ''}
        ${!canNavigate ? 'cursor-default' : ''}
        ${className}
      `}
    >
      {/* Row 1 */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span
            data-test-id={`game-title-${id}`}
            className="font-gilroy font-bold text-xl text-text-primary tracking-wide uppercase"
          >
            {title}
          </span>
        </div>

        <div className="flex-shrink-0">{getRightContent()}</div>
      </div>

      <div className="border-t-2 border-border/20 mb-4"></div>

      {/* Row 2 */}
      <div className="grid grid-cols-3 gap-4 items-end">
        <div className="flex flex-col gap-1">
          <span className="text-xs text-text-muted font-gilroy">{t('game.labels.start')}</span>
          <div className="flex items-center gap-1.5">
            {getStartIcon()}
            <span
              data-test-id={state === 'countdown' ? `countdown-timer-${id}` : `time-display-${id}`}
              className="text-sm font-gilroy font-bold text-text-primary"
            >
              {getStartDisplay()}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-xs text-text-muted font-gilroy">{t('game.labels.picks')}</span>
          <span
            data-test-id={`picks-display-${id}`}
            className="text-sm font-gilroy font-bold text-text-primary"
          >
            {getPicksDisplay()}
          </span>
        </div>

        <div className="flex flex-col gap-1 items-end">
          <>
            <div
              data-test-id={`prize-${id}`}
              className="text-sm font-gilroy font-bold pb-2"
              style={{ color: 'var(--prize-gold)' }}
            >
              {prize}
            </div>
            <div className="flex items-center gap-2">{getBottomButton()}</div>
          </>
        </div>
      </div>
    </div>
  );
};

export default GameCard;
